import { useEffect, useState } from 'react'

import type { UUID } from '@/types/Id'

export const STARTUP_GRACE_JOBS = {
  sharepointBatchImport: 'sharepoint-batch-import',
} as const

export type StartupGraceJob =
  (typeof STARTUP_GRACE_JOBS)[keyof typeof STARTUP_GRACE_JOBS]

const STARTUP_GRACE_CONFIG: Record<StartupGraceJob, { ttlMs: number }> = {
  [STARTUP_GRACE_JOBS.sharepointBatchImport]: { ttlMs: 30_000 },
}
const STARTUP_GRACE_EVENT = 'sweetspot:startup-grace'

interface StartupGraceRecord {
  expiresAt: number
}

const storageKey = (job: StartupGraceJob, orgId: UUID | '') =>
  `startup-grace:${job}:${orgId}`

const readRecord = (
  job: StartupGraceJob,
  orgId: UUID | '',
): StartupGraceRecord | null => {
  if (!orgId || typeof window === 'undefined') {
    return null
  }
  const raw = window.localStorage.getItem(storageKey(job, orgId))
  if (!raw) {
    return null
  }
  try {
    const parsed = JSON.parse(raw) as StartupGraceRecord
    if (parsed.expiresAt > Date.now()) {
      return parsed
    }
  } catch {
    // ignore malformed cache entries
  }
  window.localStorage.removeItem(storageKey(job, orgId))
  return null
}

const notify = () => {
  if (typeof window === 'undefined') {
    return
  }
  window.dispatchEvent(new Event(STARTUP_GRACE_EVENT))
}

export const markStartupGrace = (job: StartupGraceJob, orgId: UUID | '') => {
  if (!orgId || typeof window === 'undefined') {
    return
  }
  window.localStorage.setItem(
    storageKey(job, orgId),
    JSON.stringify({ expiresAt: Date.now() + STARTUP_GRACE_CONFIG[job].ttlMs }),
  )
  notify()
}

export const clearStartupGrace = (job: StartupGraceJob, orgId: UUID | '') => {
  if (!orgId || typeof window === 'undefined') {
    return
  }
  window.localStorage.removeItem(storageKey(job, orgId))
  notify()
}

export const hasStartupGrace = (job: StartupGraceJob, orgId: UUID | '') => {
  return readRecord(job, orgId) !== null
}

export const shouldPollWithStartupGrace = (
  status: string | undefined,
  startupGrace: boolean,
) => {
  return (
    status === 'pending' ||
    status === 'running' ||
    (startupGrace && status === 'not_found')
  )
}

export const shouldClearStartupGraceForStatus = (
  status: string | undefined,
) => {
  return status !== undefined && status !== 'not_found' && status !== 'pending'
}

export const withStartupGracePending = <T extends { status: string }>(
  data: T | undefined,
  startupGrace: boolean,
  overrides?: Partial<T>,
): T | undefined => {
  if (!data || data.status !== 'not_found' || !startupGrace) {
    return data
  }
  return {
    ...data,
    ...overrides,
    status: 'pending',
  }
}

export const useStartupGrace = (job: StartupGraceJob, orgId: UUID | '') => {
  const [expiresAt, setExpiresAt] = useState<number | null>(() => {
    const record = readRecord(job, orgId)
    return record?.expiresAt ?? null
  })

  const active = expiresAt != null && expiresAt > Date.now()

  useEffect(() => {
    if (!orgId || typeof window === 'undefined') {
      setExpiresAt(null)
      return
    }

    const refresh = () => {
      const record = readRecord(job, orgId)
      setExpiresAt(record?.expiresAt ?? null)
    }

    refresh()

    const onStorage = (event: StorageEvent) => {
      if (event.key === storageKey(job, orgId)) {
        refresh()
      }
    }

    window.addEventListener('storage', onStorage)
    window.addEventListener(STARTUP_GRACE_EVENT, refresh)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(STARTUP_GRACE_EVENT, refresh)
    }
  }, [job, orgId])

  useEffect(() => {
    if (expiresAt == null || typeof window === 'undefined') {
      return
    }
    const timeout = window.setTimeout(
      () => {
        setExpiresAt(null)
        clearStartupGrace(job, orgId)
      },
      Math.max(0, expiresAt - Date.now()),
    )

    return () => {
      window.clearTimeout(timeout)
    }
  }, [expiresAt, job, orgId])

  return active
}
